import React, {useState, useEffect} from 'react'
import CloseIcon from '@mui/icons-material/Close'
import Grid from '@mui/material/Grid'
import {StyledDialogTitle, StyledIconButton} from '../RemoveUser/RemoveUserStyles'
import {
  SaveButton,
  CancelButton,
  StyledDivFooter,
  StyledGrid,
  StyledDialogContent,
  StyledTextField,
  StyledDialog,
  StyledTemporaryAccessRow,
  StyledInputTitleInline,
  StyledEndDateWrapper
} from './EditUserStyle'
import CustomBox from '../../../components/common/CustomBox'
import PopupMessages from '../../common/PopupMessages/PopupMessages.json'
import {handleInput} from '../../common/Helpers/validator'
import buttonTitles from '../../../appConfig/buttonTitles.config.json'
import PropTypes from 'prop-types'
import StartDatePickerComponent from '../StartDatePickerComponent'
import EndDatePickerComponent from '../EndDatePickerComponent'
import dayjs from 'dayjs'
import {StyledTooltip, TemporaryToggle, InputError, StyledToolTipIconButton} from '../UserManagementStyle'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import InputAdornment from '@mui/material/InputAdornment'
import Typography from '@mui/material/Typography'

export const formatDate = (date) => {
  if (date === 'Indefinite') {
    return null
  }
  return dayjs(date)?.format('YYYY-MM-DD')
}
export const createObjHelper = (data, firstName, lastName, startDate, endDate, checked) => {
  const updateObj = {}
  if (firstName !== data.FIRST_NAME) {
    Object.assign(updateObj, {firstName})
  }
  if (lastName !== data.LAST_NAME) {
    Object.assign(updateObj, {lastName})
  }
  if (formatDate(startDate) !== formatDate(data.START_DATE)) {
    Object.assign(updateObj, {startDate: formatDate(startDate)})
  }
  if (checked !== data.IS_TEMPORARY) {
    Object.assign(updateObj, {isTemporary: checked})
  }
  if (checked && formatDate(endDate) !== formatDate(data.END_DATE)) {
    Object.assign(updateObj, {endDate: formatDate(endDate)})
  }
  if (Object.keys(updateObj).length !== 0) {
    Object.assign(updateObj, {userEmail: data.EMAIL_ID, rowId: data.ID})
  }
  return updateObj
}

const EditUserComponent = ({open, setOpen, data, handleEditUser, isClient}) => {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [errorFirstNameTextEnabled, setErrorFirstNameTextEnabled] = useState(false)
  const [errorLastNameTextEnabled, setErrorLastNameTextEnabled] = useState(false)
  const [errorFirstNameText, setErrorFirstNameText] = React.useState('')
  const [errorLastNameText, setErrorLastNameText] = React.useState('')
  const [disableSave, setDisableSave] = useState(true)
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)
  const [checked, setChecked] = useState(false)
  const [errorEndDateTextEnabled, setErrorEndDateTextEnabled] = useState(false)
  const [errorEndDateText, setErrorEndDateText] = useState('')
  const [updateUserPayload, setUpdateUserPayload] = useState({})

  const tempUserTooltipText = 'Temporary users have an Access End Date.'

  const handleClose = () => {
    setOpen(false)
  }

  const handleChange = () => {
    setChecked(!checked)
  }

  useEffect(() => {
    const tempChanged = checked !== data.IS_TEMPORARY
    if (!tempChanged) {
      setStartDate(data.START_DATE)
      setEndDate(data.END_DATE)
      setErrorEndDateText(false)
      setErrorEndDateText('')
      return
    }
    setStartDate(dayjs().startOf('day'))
    if (checked && (!endDate || endDate === 'Indefinite')) {
      setErrorEndDateTextEnabled(true)
      setErrorEndDateText('Please select an end date.')
    } else {
      !checked && setEndDate(null)
      setErrorEndDateText(false)
      setErrorEndDateText('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked])
  const handleEdit = async () => {
    setOpen(false)
    setErrorEndDateTextEnabled(false)
    setErrorEndDateText('')

    let hasError = false
    if (checked && !endDate) {
      setErrorEndDateTextEnabled(true)
      setErrorEndDateText('Please select an end date.')
      hasError = true
    }

    if (hasError) {
      setOpen(true)
    } else {
      const {firstName, lastName, userEmail, startDate, endDate, isTemporary, rowId} = updateUserPayload
      handleEditUser(firstName, lastName, userEmail, startDate, endDate, isTemporary, rowId)
    }
  }
  const createUpdateObject = () => {
    const updateObj = createObjHelper(data, firstName, lastName, startDate, endDate, checked)
    setUpdateUserPayload(updateObj)
  }

  React.useEffect(() => {
    let isSameData
    if (checked) {
      isSameData =
        firstName === data.FIRST_NAME &&
        lastName === data.LAST_NAME &&
        formatDate(startDate) === formatDate(data.START_DATE) &&
        formatDate(endDate) === formatDate(data.END_DATE) &&
        checked === data.IS_TEMPORARY
    } else {
      isSameData =
        firstName === data.FIRST_NAME &&
        lastName === data.LAST_NAME &&
        formatDate(startDate) === formatDate(data.START_DATE) &&
        checked === data.IS_TEMPORARY
    }
    setDisableSave(isSameData)
    createUpdateObject() // Creates an object for API call with only the changed/updated values
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstName, lastName, startDate, endDate, checked, data])

  React.useEffect(() => {
    if (data) {
      setFirstName(data.FIRST_NAME)
      setLastName(data.LAST_NAME)
      setStartDate(data.START_DATE)
      setEndDate(data.END_DATE)
      setChecked(data.IS_TEMPORARY)
    }
  }, [data])

  const tooltipTitle = "User's email cannot be edited"

  const [isTemporaryFieldToolTipOpen, setIsTemporaryFieldToolTipOpen] = useState(false)
  const handleTemporaryFieldClickAway = () => {
    setIsTemporaryFieldToolTipOpen(false)
  }

  const [isEmailFieldToolTipOpen, setIsEmailFieldToolTipOpen] = useState(false)
  const handleEmailFieldClickAway = () => {
    setIsEmailFieldToolTipOpen(false)
  }

  const handleFirstNameBlur = () => {
    const trimmed = firstName.trim()
    const capitalizedFirstName = trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
    setFirstName(capitalizedFirstName)
  }
  const handleLastNameBlur = () => {
    const trimmed = lastName.trim()
    const capitalizedLastName = trimmed.charAt(0).toUpperCase() + trimmed.slice(1)
    setLastName(capitalizedLastName)
  }
  return (
    <StyledDialog maxWidth='md' onClose={handleClose} aria-labelledby='edit-user-dialog-title' open={open}>
      <StyledDialogTitle id='edit-user-dialog-title'>
        {PopupMessages.editUserTitle}
        {
          <StyledIconButton title={buttonTitles.popupNotification} onClick={handleClose} aria-label='close'>
            <CloseIcon variant='popOverCloseIcon' />
          </StyledIconButton>
        }
      </StyledDialogTitle>
      <StyledDialogContent id='edit-header' dividers>
        {PopupMessages.editUserBody} {data.FIRST_NAME} {data.LAST_NAME}
        <StyledGrid container spacing={3}>
          <Grid item xs={12} lg={6}>
            <StyledTextField
              required
              label='First Name'
              variant='outlined'
              fullWidth
              aria-label={firstName}
              type='text'
              name='firstname'
              value={firstName}
              error={errorFirstNameTextEnabled}
              placeholder={data.FIRST_NAME}
              onChange={(e) => {
                handleInput(
                  setFirstName,
                  setErrorFirstNameTextEnabled,
                  setErrorFirstNameText,
                  true,
                  [], // to avoid duplicate name check
                  data.FIRST_NAME
                )(e)
              }}
              onBlur={handleFirstNameBlur}
            />
            <CustomBox tabIndex={0} hidden={!errorFirstNameTextEnabled} aria-label={errorFirstNameText}>
              <InputError id='edit-first-name-error' aria-live='polite'>
                {errorFirstNameTextEnabled && errorFirstNameText}
              </InputError>
            </CustomBox>
          </Grid>
          <Grid item xs={12} lg={6}>
            <StyledTextField
              label='Last Name'
              variant='outlined'
              fullWidth
              aria-label={lastName}
              type='text'
              name='lastname'
              value={lastName}
              error={errorLastNameTextEnabled}
              placeholder={data.LAST_NAME}
              onChange={(e) => {
                handleInput(
                  setLastName,
                  setErrorLastNameTextEnabled,
                  setErrorLastNameText,
                  true,
                  [], // to avoid duplicate name check
                  data.LAST_NAME
                )(e)
              }}
              onBlur={handleLastNameBlur}
            />
            <CustomBox tabIndex={0} hidden={!errorLastNameTextEnabled} aria-label={errorLastNameText}>
              <InputError id='edit-last-name-error' aria-live='polite'>
                {errorLastNameTextEnabled && errorLastNameText}
              </InputError>
            </CustomBox>
          </Grid>

          <Grid item xs={12}>
            <StyledTextField
              label='Email address'
              variant='outlined'
              fullWidth
              value={data.EMAIL_ID}
              disabled
              aria-label={data.EMAIL_ID}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <StyledTooltip
                      enterTouchDelay={0}
                      leaveTouchDelay={6000}
                      title={tooltipTitle}
                      open={isEmailFieldToolTipOpen}
                      onClickAway={handleEmailFieldClickAway}
                      disableHoverListener={true}
                      disableFocusListener={true}
                      aria-describedby='email-address-field-tooltip'
                    >
                      <StyledToolTipIconButton
                        onClick={() => setIsEmailFieldToolTipOpen(!isEmailFieldToolTipOpen)}
                        id='email-address-field-tooltip'
                        aria-label={isEmailFieldToolTipOpen ? tooltipTitle : 'Toggle Email Address Field Tooltip'}
                        aria-controls='email-address-field-tooltip'
                        aria-expanded={isEmailFieldToolTipOpen}
                      >
                        <InfoOutlinedIcon variant='primaryIcon' />
                      </StyledToolTipIconButton>
                    </StyledTooltip>
                  </InputAdornment>
                )
              }}
            />
          </Grid>

          {!isClient && (
            <>
              {' '}
              <Grid item xs={12}>
                <StyledTemporaryAccessRow>
                  <StyledInputTitleInline>
                    Temporary Access{' '}
                    <StyledTooltip
                      enterTouchDelay={0}
                      leaveTouchDelay={6000}
                      title={tempUserTooltipText}
                      open={isTemporaryFieldToolTipOpen}
                      onClickAway={handleTemporaryFieldClickAway}
                      disableHoverListener={true}
                      disableFocusListener={true}
                      aria-describedby='temporary-date-field-tooltip'
                    >
                      <StyledToolTipIconButton
                        onClick={() => setIsTemporaryFieldToolTipOpen(!isTemporaryFieldToolTipOpen)}
                        id='temporary-date-field-tooltip'
                        aria-label={
                          isTemporaryFieldToolTipOpen ? tempUserTooltipText : 'Toggle Temporary End Date Field Tooltip'
                        }
                        aria-controls='temporary-date-field-tooltip'
                        aria-expanded={isTemporaryFieldToolTipOpen}
                      >
                        <InfoOutlinedIcon variant='primaryIcon' />
                      </StyledToolTipIconButton>
                    </StyledTooltip>
                  </StyledInputTitleInline>
                  <Typography variant='body2' color='textSecondary'>
                    Off
                  </Typography>
                  <TemporaryToggle
                    data-testid='Switch-for-changing-user-type'
                    checked={checked}
                    onChange={handleChange}
                    inputProps={{'aria-label': 'Temporary Access Switch Button'}}
                  />
                  <Typography variant='body2' color='textSecondary'>
                    On
                  </Typography>
                </StyledTemporaryAccessRow>
              </Grid>
              <Grid item xs={6} lg={6}>
                <div>
                  <StartDatePickerComponent
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    isProjectCompleted={true}
                  />
                </div>
              </Grid>
              <Grid item xs={6} lg={6}>
                <StyledEndDateWrapper checked={checked}>
                  <EndDatePickerComponent
                    startDate={startDate}
                    endDate={endDate}
                    setEndDate={(date) => {
                      setEndDate(date)
                      if (errorEndDateTextEnabled && date) {
                        setErrorEndDateTextEnabled(false)
                        setErrorEndDateText('')
                      }
                    }}
                    checked={checked}
                  />
                  {errorEndDateTextEnabled && (
                    <CustomBox tabIndex={0} aria-label={errorEndDateText}>
                      <InputError>{errorEndDateText}</InputError>
                    </CustomBox>
                  )}
                </StyledEndDateWrapper>
              </Grid>
            </>
          )}
        </StyledGrid>
        <StyledDivFooter>
          <CancelButton onClick={handleClose}>Cancel</CancelButton>
          <SaveButton
            aria-label={`edit-user, ${firstName} ${lastName}`}
            onClick={handleEdit}
            disabled={disableSave || errorFirstNameTextEnabled || errorLastNameTextEnabled || errorEndDateTextEnabled}
          >
            Save
          </SaveButton>
        </StyledDivFooter>
      </StyledDialogContent>
    </StyledDialog>
  )
}

EditUserComponent.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  handleEditUser: PropTypes.func.isRequired,
  isClient: PropTypes.bool
}

export default EditUserComponent

/* Function Descriptions:
  - formatDate: Formats a date to 'YYYY-MM-DD' format or returns null for 'Indefinite'.
  - createObjHelper: Creates an object for API call with only the changed/updated values.
  - EditUserComponent: Main component that renders the edit user dialog, including input fields for first name, last name, email, and date pickers for start and end dates.
    - handleClose: Closes the dialog.
    - handleChange: Toggles the temporary user checkbox.
    - useEffect 1: Sets the start and end dates based on the temporary user checkbox state.
    - useEffect 2: Creates an object for API call with only the changed/updated values.
    - useEffect 3: Sets the initial state of the input fields based on the passed data prop.
*/
